/**
 * projectIntent — resolved-view helpers around `ProjectIntent.powerRequirement`
 * + `powerRequirementMW` (Project_Intent_Exact_MW_Prompt pack).
 *
 * The intent type stores BOTH a coarse bucket (`powerRequirement`) and an
 * optional exact MW (`powerRequirementMW`). When the user knows the precise
 * number we want every reader — LLM prompts, narrative copy, LCOE seeding,
 * dashboard rows — to use it. Bucket-based consumers (scoring tiers,
 * use-case derivation) keep working because the bucket stays authoritative
 * and is auto-derived from MW via `bucketFromMw()` whenever MW is set.
 *
 * Discipline: any writer that sets `powerRequirementMW` MUST also set
 * `powerRequirement = bucketFromMw(mw)` so the two fields never disagree.
 * The form/store callsites do this via `withPowerRequirementMW()` /
 * `withPowerRequirementBucket()` below.
 */
import type {
  ProjectIntent,
  ProjectPhase,
  PowerSourceKey,
  PhasePowerComponentKey,
  PhasePowerSourceConfig,
  PhaseCodTimeline,
} from '../types/siteRag';
import {
  POWER_SOURCE_LABEL,
  PHASE_POWER_COMPONENT_LABEL,
} from '../types/siteRag';
import { normaliseCooling, type CoolingType } from '../types/cooling';

/**
 * Power-requirement bucket ladder (extended to 1500 MW).
 *
 * `over-200mw` is kept ONLY as a backward-compat legacy value for intents
 * stored before the ladder was refined — `bucketFromMw()` never emits it
 * anymore (see below) and the Range-bucket picker never offers it, but every
 * Record map / midpoint table still accepts it so old data typechecks and
 * renders.
 */
export type PowerBucket =
  | 'under-10mw'
  | '10-50mw'
  | '50-200mw'
  | '200-500mw'
  | '500-1000mw'
  | '1000-1500mw'
  | 'over-1500mw'
  | 'over-200mw' // legacy — back-compat only; never emitted by bucketFromMw()
  | 'undecided';

/** The buckets a fresh entry can be assigned — excludes the legacy
 *  `over-200mw` value. Order = ladder order, used by the Range picker. */
export const POWER_BUCKET_VALUES = [
  'under-10mw',
  '10-50mw',
  '50-200mw',
  '200-500mw',
  '500-1000mw',
  '1000-1500mw',
  'over-1500mw',
  'undecided',
] as const satisfies ReadonlyArray<PowerBucket>;

/** Inclusive lower bound at which the next bucket starts. Boundaries pick
 *  the higher bucket (e.g. exactly 50 MW → '50-200mw') to match the way
 *  users read the labels "10–50" / "50–200". NEVER returns the legacy
 *  'over-200mw' value — that band is now split into finer buckets. */
export function bucketFromMw(mw: number | undefined | null): PowerBucket {
  if (mw == null || !Number.isFinite(mw) || mw <= 0) return 'undecided';
  if (mw < 10) return 'under-10mw';
  if (mw < 50) return '10-50mw';
  if (mw < 200) return '50-200mw';
  if (mw < 500) return '200-500mw';
  if (mw < 1000) return '500-1000mw';
  if (mw < 1500) return '1000-1500mw';
  return 'over-1500mw';
}

/** Display label for the coarse bucket — single source of truth so form,
 *  summary, and read-mode cards all match. */
export const POWER_BUCKET_LABEL: Record<PowerBucket, string> = {
  'under-10mw':   'Under 10 MW',
  '10-50mw':      '10–50 MW',
  '50-200mw':     '50–200 MW',
  '200-500mw':    '200–500 MW',
  '500-1000mw':   '500–1000 MW',
  '1000-1500mw':  '1000–1500 MW',
  'over-1500mw':  'Over 1500 MW',
  'over-200mw':   '200 MW+ (legacy)',
  'undecided':    'Undecided',
};

/**
 * Bucket → MW basis (single source of truth). Per spec this is the MIDPOINT
 * of each closed bucket, the MAX for the lowest open-low bucket, and the
 * floor for the open-high bucket. Imported by SCM BoM, generation sizing,
 * preference inference, and `firmCapacityBasisMW` derivation so every
 * "bucket-only → MW" projection agrees.
 *
 *   under-10mw   → 10   (max of the open-low band)
 *   10-50mw      → 30   (midpoint)
 *   50-200mw     → 125  (midpoint)
 *   200-500mw    → 350  (midpoint)
 *   500-1000mw   → 750  (midpoint)
 *   1000-1500mw  → 1250 (midpoint)
 *   over-1500mw  → 1500 (floor of the open-high band)
 *   over-200mw   → 300  (legacy midpoint — preserved for stored intents)
 */
export const POWER_BUCKET_MIDPOINT_MW: Record<Exclude<PowerBucket, 'undecided'>, number> = {
  'under-10mw':   10,
  '10-50mw':      30,
  '50-200mw':     125,
  '200-500mw':    350,
  '500-1000mw':   750,
  '1000-1500mw':  1250,
  'over-1500mw':  1500,
  'over-200mw':   300, // legacy
};

/**
 * WHICH RULE produced each entry of `POWER_BUCKET_MIDPOINT_MW`.
 *
 * The table above is NOT uniformly a midpoint — it applies three different
 * rules, and a UI that labels all of them "midpoint" would be lying:
 *
 *   under-10mw  → 10   is the band's MAXIMUM   (open-low band, sits at the TOP)
 *   over-1500mw → 1500 is the band's FLOOR     (open-high band, sits at the BOTTOM)
 *   everything else    is a true MIDPOINT
 *
 * Kept adjacent to `POWER_BUCKET_MIDPOINT_MW` so one test pins both tables and
 * they can never drift. Consumed by the power-stack band resolver to label the
 * sizing basis honestly.
 */
export type SizingBasisRule =
  | 'bucket-midpoint'
  | 'bucket-max-open-low'
  | 'bucket-floor-open-high';

export const SIZING_BASIS_RULE: Record<Exclude<PowerBucket, 'undecided'>, SizingBasisRule> = {
  'under-10mw':   'bucket-max-open-low',
  '10-50mw':      'bucket-midpoint',
  '50-200mw':     'bucket-midpoint',
  '200-500mw':    'bucket-midpoint',
  '500-1000mw':   'bucket-midpoint',
  '1000-1500mw':  'bucket-midpoint',
  'over-1500mw':  'bucket-floor-open-high',
  'over-200mw':   'bucket-floor-open-high', // legacy [200, ∞) — 300 is inside, but the band is open-high
};

/** Map a bucket to its MW basis (midpoint/max/floor per the table above).
 *  Returns undefined for `undecided` / undefined so callers can treat
 *  "no signal" uniformly. Pure. */
export function bucketMidpointMw(
  bucket: PowerBucket | undefined | null,
): number | undefined {
  if (!bucket || bucket === 'undecided') return undefined;
  return POWER_BUCKET_MIDPOINT_MW[bucket];
}

/** Single resolved view used everywhere reads happen. */
export interface EffectivePower {
  /** Exact value when `powerRequirementMW` was set; null otherwise. */
  mw: number | null;
  /** Always populated — derived from `mw` when set, else the stored bucket. */
  bucket: PowerBucket;
  /** True when `powerRequirementMW` was set explicitly. */
  isExact: boolean;
  /** "350 MW" if exact, else the bucket label ("200+ MW", "Undecided", …). */
  displayLabel: string;
}

/** Resolve the precision-preserving view of an intent's power requirement.
 *  Accepts a partial so callers can pass typed-preference slices, audit
 *  diffs, or other shapes that carry just the two fields. */
export function effectivePower(
  intent: Pick<ProjectIntent, 'powerRequirement' | 'powerRequirementMW'> | undefined | null,
): EffectivePower {
  const mw = intent?.powerRequirementMW;
  if (typeof mw === 'number' && Number.isFinite(mw) && mw > 0) {
    return {
      mw,
      bucket: bucketFromMw(mw),
      isExact: true,
      displayLabel: formatMw(mw),
    };
  }
  const bucket = (intent?.powerRequirement ?? 'undecided') as PowerBucket;
  return {
    mw: null,
    bucket,
    isExact: false,
    displayLabel: POWER_BUCKET_LABEL[bucket] ?? POWER_BUCKET_LABEL.undecided,
  };
}

/** Trim trailing zeros so 350 → "350 MW", 7.5 → "7.5 MW", 7.50 → "7.5 MW". */
function formatMw(mw: number): string {
  const rounded = Math.round(mw * 10) / 10;
  const text = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toString();
  return `${text} MW`;
}

/**
 * WPG-3 — bucket → [min, max] MW range table. Mirrors `bucketFromMw`'s
 * boundary convention (the higher bucket wins at exact boundaries).
 * `undecided` returns null so callers can short-circuit divergence
 * checks when the intent figure itself is unknown.
 *
 * Used by `flagCapacityDrift` (narrativeHygiene.ts) to compare a
 * narrative MW figure against the intent range. Centralising the table
 * here keeps the gen-time prompt's `parseIntentMwRange` (reportPrompts.ts)
 * and the post-gen guard in lockstep without either side reaching into
 * the other's internals.
 */
const _BUCKET_MW_RANGE: Record<Exclude<PowerBucket, 'undecided'>, { min: number; max: number }> = {
  'under-10mw':   { min: 0, max: 10 },
  '10-50mw':      { min: 10, max: 50 },
  '50-200mw':     { min: 50, max: 200 },
  '200-500mw':    { min: 200, max: 500 },
  '500-1000mw':   { min: 500, max: 1000 },
  '1000-1500mw':  { min: 1000, max: 1500 },
  'over-1500mw':  { min: 1500, max: Number.POSITIVE_INFINITY },
  'over-200mw':   { min: 200, max: Number.POSITIVE_INFINITY }, // legacy
};

/**
 * Bucket → `[min, max]` MW range (public accessor over the SSOT table
 * above). Returns null for `undecided` / absent so callers can treat
 * "no signal" uniformly. Open bands carry `0` / `Infinity` edges — band
 * consumers (`resolvePowerStackBand`) null those out via `positiveOrNull`.
 * Pure; callers must treat the returned object as read-only.
 */
export function bucketMwRange(
  bucket: PowerBucket | undefined | null,
): { min: number; max: number } | null {
  if (!bucket || bucket === 'undecided') return null;
  return _BUCKET_MW_RANGE[bucket];
}

/**
 * WPG-3 helper — resolve the `[min, max]` MW range from an
 * `EffectivePower` view. When the intent has an exact MW, the range
 * collapses to a single point ({mw, mw}); when only a bucket is
 * known, the bucket's range is returned; when `undecided`, returns
 * null (skip divergence check entirely — Risk-R7 mitigation).
 */
export function effectivePowerMwRange(
  ep: EffectivePower,
): { min: number; max: number } | null {
  if (ep.isExact && ep.mw != null && Number.isFinite(ep.mw) && ep.mw > 0) {
    return { min: ep.mw, max: ep.mw };
  }
  return bucketMwRange(ep.bucket);
}

/** Writer helper — sets `powerRequirementMW` and recomputes the bucket in
 *  one go. Use this from form onChange / store actions so the two fields
 *  never drift apart. */
export function withPowerRequirementMW(intent: ProjectIntent, mw: number): ProjectIntent {
  return {
    ...intent,
    powerRequirementMW: mw,
    powerRequirement: bucketFromMw(mw),
  };
}

/**
 * V134-BUCKET-REBUILD — reconcile-on-save guard for the coarse
 * `powerRequirement` bucket (the non-phased analogue of
 * `reconcilePowerRequirementWithPhases`). When an exact MW is present,
 * forces the stored bucket to agree with `bucketFromMw(mw)` so the two
 * fields can never drift apart on disk — even if a caller persists a
 * hand-built intent whose bucket is stale (e.g. MW=1750 but the bucket left
 * at the legacy 'over-200mw' from a prior bucket-only edit). This is the
 * symptom behind the Coverage Matrix showing 'over-200mw' for a 1750 MW
 * site.
 *
 * NORMALIZATION of the incoming payload against ITSELF — it reads nothing
 * from previously stored state, so it preserves the wq07loep2 verbatim-write
 * doctrine (it can't resurrect a cleared field). An unset/cleared MW is a
 * no-op (a deliberate bucket-only intent is honored). Pure: returns a new
 * object only when a correction is needed, otherwise the same reference.
 */
export function reconcilePowerRequirement<
  T extends Pick<ProjectIntent, 'powerRequirement' | 'powerRequirementMW'>,
>(intent: T): T {
  const mw = intent.powerRequirementMW;
  if (typeof mw !== 'number' || !Number.isFinite(mw) || mw <= 0) return intent;
  const reconciled = bucketFromMw(mw);
  if (intent.powerRequirement === reconciled) return intent;
  return { ...intent, powerRequirement: reconciled };
}

/** Writer helper — clears the exact MW and sets the bucket directly. Use
 *  when the user switches the form back to Range mode. */
export function withPowerRequirementBucket(intent: ProjectIntent, bucket: PowerBucket): ProjectIntent {
  const { powerRequirementMW: _drop, ...rest } = intent;
  void _drop;
  return {
    ...rest,
    powerRequirement: bucket,
  };
}

// ───────────────────────────────────────────────────────────────────────────
// Timeline — exact in-service date / bucket pair (PMT pack).
//
// Mirrors the powerRequirement / powerRequirementMW pair above. Intent
// carries BOTH a coarse bucket (`timeline`) and an optional exact
// in-service date (`inServiceDate`). When the user knows the date we
// want every downstream reader — LLM prompts, narrative copy, SCM
// critical-path math, dashboard chips — to use it. Bucket-based
// consumers keep working because the bucket stays authoritative and is
// auto-derived from the date via `bucketFromInServiceDate()` whenever
// a date is set.
//
// Discipline: any writer that sets `inServiceDate` MUST also set
// `timeline = bucketFromInServiceDate(date, now)` so the two fields
// never disagree. The form/store callsites do this via
// `withInServiceDate()` / `withTimelineBucket()` below.
// ───────────────────────────────────────────────────────────────────────────

export type TimelineBucket = 'under-2yr' | '2-5yr' | 'over-5yr' | 'undecided';

/** Approximate days-per-year — matches the conventional value used
 *  throughout the codebase for month/year arithmetic. */
const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;
/** Conventional month length (365.25 / 12 days) — same factor used
 *  in `criticalPathAnalysis.ts` so the two stay numerically aligned. */
const MS_PER_MONTH = (365.25 / 12) * 24 * 60 * 60 * 1000;

/** Map an ISO date to a coarse timeline bucket. Boundaries pick the
 *  later bucket inclusively (e.g. exactly 2.0 years → '2-5yr', exactly
 *  5.0 years → '2-5yr') to match the way users read the labels
 *  "< 2 years" / "2-5 years" / "5+ years". Past dates resolve to
 *  `undecided` — a date already gone is not informative.
 */
export function bucketFromInServiceDate(
  inServiceDate: string | undefined | null,
  now: Date = new Date(),
): TimelineBucket {
  if (!inServiceDate) return 'undecided';
  const target = new Date(inServiceDate);
  if (!Number.isFinite(target.getTime())) return 'undecided';
  const yearsAway = (target.getTime() - now.getTime()) / MS_PER_YEAR;
  if (yearsAway < 0) return 'undecided';
  if (yearsAway < 2) return 'under-2yr';
  if (yearsAway <= 5) return '2-5yr';
  return 'over-5yr';
}

/** Display label for the coarse timeline bucket — single source of truth
 *  so form, summary, and read-mode cards all match. */
export const TIMELINE_BUCKET_LABEL: Record<TimelineBucket, string> = {
  'under-2yr': '< 2 years',
  '2-5yr':     '2-5 years',
  'over-5yr':  '5+ years',
  'undecided': 'Undecided',
};

/** Resolved view used everywhere reads happen — mirrors `EffectivePower`. */
export interface EffectiveTimeline {
  /** ISO date when `inServiceDate` was set; null otherwise. */
  inServiceDate: string | null;
  /** Always populated — derived from `inServiceDate` when set, else the stored bucket. */
  bucket: TimelineBucket;
  /** True when `inServiceDate` was set explicitly (and parses to a valid future-ish date). */
  isExact: boolean;
  /** `"Apr 2028"` when exact; bucket label (`"2-5 years"`, `"Undecided"`, …) otherwise. */
  displayLabel: string;
  /**
   * Months between `now` and `inServiceDate`. Negative when the date
   * has already passed. `null` when `inServiceDate` is absent or
   * unparseable — bucket-only callers should fall back to a bucket
   * midpoint (see `criticalPathAnalysis` / `supplyChainRisks`).
   */
  monthsAway: number | null;
}

/** Resolve the precision-preserving view of an intent's timeline.
 *  Accepts a partial so callers can pass typed-preference slices,
 *  audit diffs, or other shapes that carry just the two fields. */
export function effectiveTimeline(
  intent: Pick<ProjectIntent, 'timeline' | 'inServiceDate'> | undefined | null,
  now: Date = new Date(),
): EffectiveTimeline {
  const iso = intent?.inServiceDate;
  if (iso) {
    const target = new Date(iso);
    if (Number.isFinite(target.getTime())) {
      const monthsAway = Math.round(
        (target.getTime() - now.getTime()) / MS_PER_MONTH,
      );
      return {
        inServiceDate: iso,
        bucket: bucketFromInServiceDate(iso, now),
        isExact: true,
        displayLabel: formatInServiceDate(iso),
        monthsAway,
      };
    }
  }
  const bucket = (intent?.timeline ?? 'undecided') as TimelineBucket;
  return {
    inServiceDate: null,
    bucket,
    isExact: false,
    displayLabel: TIMELINE_BUCKET_LABEL[bucket] ?? TIMELINE_BUCKET_LABEL.undecided,
    monthsAway: null,
  };
}

/** Format an ISO yyyy-mm-dd as `"Apr 2028"`. The full ISO survives in
 *  the data model so hover-tooltips can show the precise day; the
 *  display string is intentionally month-precision because per-day
 *  COD precision is rarely meaningful at this stage of planning. */
export function formatInServiceDate(iso: string): string {
  if (/^\d{4}$/.test(iso.trim())) return iso.trim();
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  // `inServiceDate` is DATE-ONLY (`yyyy-mm-dd` from a date input). ECMAScript
  // parses that as UTC midnight, so rendering it without an explicit zone
  // resolves in the HOST zone and shows the PREVIOUS day on any host west of
  // Greenwich — '2028-04-01' came back "Mar 2028", and '2026-01-01' came back
  // "Dec 2025". Pin the zone to UTC so the label is the day the value denotes,
  // not a property of the machine. Format options are otherwise unchanged.
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** Writer helper — sets `inServiceDate` and recomputes the bucket in
 *  one go. Use this from form onChange / store actions so the two
 *  fields never drift apart. Mirrors `withPowerRequirementMW()`. */
export function withInServiceDate(
  intent: ProjectIntent,
  inServiceDate: string,
  now: Date = new Date(),
): ProjectIntent {
  return {
    ...intent,
    inServiceDate,
    timeline: bucketFromInServiceDate(inServiceDate, now),
  };
}

/** Writer helper — clears the exact date and sets the bucket directly.
 *  Use when the user switches the form back to Range mode. Mirrors
 *  `withPowerRequirementBucket()`. */
export function withTimelineBucket(
  intent: ProjectIntent,
  bucket: TimelineBucket,
): ProjectIntent {
  const { inServiceDate: _drop, ...rest } = intent;
  void _drop;
  return {
    ...rest,
    timeline: bucket,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Project Phasing — effective view helpers (PP pack, PP2).
//
// Extends the `effectivePower` pattern so phased and single-phase intents
// share a single uniform read shape. Callers never branch on whether the
// project is phased; they call `effectivePhases(intent)` and walk the
// resulting `phases[]`.
//
// Single-phase contract: when `intent.phases` is undefined or empty, the
// helper returns a synthesised one-element [{capacityMW, COD, …}] derived
// from the top-level intent (powerRequirementMW + inServiceDate). This
// preserves back-compat — every caller that reads the phased view also
// works for legacy sites.
// ─────────────────────────────────────────────────────────────────────────────

export interface EffectivePhasedView {
  /** True when `intent.phases` has at least one entry. */
  isPhased: boolean;
  /** Count of phases (1 for legacy single-phase mode). */
  phaseCount: number;
  /**
   * Phases sorted by `sequence`. ALWAYS at least one entry — for legacy
   * single-phase intents we synthesise a one-element array so consumers
   * (renderers, prompts, SCM, finance) can iterate without branching.
   */
  phases: ProjectPhase[];
  /**
   * ULTIMATE campus capacity in MW — the final phase's CUMULATIVE figure
   * (defensively `Math.max` over finite capacities; never a sum). Null when
   * no phase carries a capacity (all TBD) — AND null when the FINAL phase
   * carries a `capacityBucket` range instead of an exact figure: the campus
   * total is then honestly a RANGE (`ultimateCapacityBucket`), and a range
   * never collapses into a scalar. For single-phase mode this equals
   * `intent.powerRequirementMW ?? null`.
   */
  totalCapacityMW: number | null;
  /**
   * The final phase's capacity RANGE bucket, when it carries one instead of
   * an exact figure. Non-null ⇒ `totalCapacityMW` is null and the campus
   * figure resolves as a band (`resolveIntentPowerStackBand`). Null on
   * exact-ultimate, drafting-ultimate, and single-phase intents.
   */
  ultimateCapacityBucket: Exclude<PowerBucket, 'undecided'> | null;
  /**
   * Largest finite positive cumulative capacity across any declared phase,
   * even when the final phase is TBD (`totalCapacityMW === null`). Used to
   * distinguish "known earlier-phase floor with final phase TBD" from
   * "all phases drafting (`null` MW)".
   */
  knownCapacityFloorMW: number | null;
  /** Phase-1 capacity in MW. Same fallback rules as `totalCapacityMW`. */
  initialCapacityMW: number | null;
  /** Latest phase COD across all phases (ISO date). Null when the final phase has no exact date (TBD or planning-target timeline bucket). */
  ultimateInServiceDate: string | null;
  /** The final phase's timeline RANGE bucket (e.g. 2-5yr planning target), when it carries one instead of an exact targetInServiceDate. Non-null => ultimateInServiceDate is null so the initial phase's exact COD is not misreported as the final phase's COD. */
  ultimateCodTimeline: Exclude<PhaseCodTimeline, 'undecided'> | null;
  /** Latest known ISO in-service date across any phase, even when the final phase has no exact date (`ultimateInServiceDate === null`). */
  lastKnownInServiceDate: string | null;
  /** Phase-1 COD (ISO date). Null when phase-1 has no date. */
  initialInServiceDate: string | null;
  /**
   * Unique non-undecided power sources, in phase order. Empty array when
   * every phase is `undecided` or in single-phase mode (single-phase has
   * no per-phase powerSource semantics).
   */
  powerSourceMix: PowerSourceKey[];
  /**
   * Human-readable tech-mix summary — "Grid → Gas CC → SMR" for phased,
   * "Single-phase" for legacy.
   */
  techMixSummary: string;
}

/**
 * Resolve the phased view of a ProjectIntent. Always returns a valid
 * view — legacy single-phase intents collapse to a one-element synthetic
 * phase array so callers can iterate uniformly.
 *
 * Synthetic single-phase rules:
 *   - id: 'single-phase'
 *   - sequence: 1, powerSource: 'undecided'
 *   - capacityMW from `powerRequirementMW`, date from `inServiceDate`
 *
 * The synthetic phase is identifiable via `view.isPhased === false` —
 * code that needs to distinguish should check that flag rather than
 * inspect `phases.length`.
 */
export function effectivePhases(intent: ProjectIntent | undefined | null): EffectivePhasedView {
  const rawPhases = (intent?.phases ?? []).slice().sort((a, b) => a.sequence - b.sequence);

  if (rawPhases.length === 0) {
    // Legacy single-phase synthesis. The single phase echoes whatever the
    // user has set at the top level — `null` when not set, never zero.
    const mw = (typeof intent?.powerRequirementMW === 'number' && Number.isFinite(intent.powerRequirementMW) && intent.powerRequirementMW > 0)
      ? intent.powerRequirementMW
      : null;
    const cod = intent?.inServiceDate ?? null;
    const synthetic: ProjectPhase = {
      id: 'single-phase',
      label: 'Phase 1',
      sequence: 1,
      capacityMW: mw,
      targetInServiceDate: cod,
      powerSource: 'undecided',
    };
    return {
      isPhased: false,
      phaseCount: 1,
      phases: [synthetic],
      totalCapacityMW: mw,
      ultimateCapacityBucket: null,
      knownCapacityFloorMW: mw,
      initialCapacityMW: mw,
      ultimateInServiceDate: cod,
      ultimateCodTimeline: null,
      lastKnownInServiceDate: cod,
      initialInServiceDate: cod,
      powerSourceMix: [],
      techMixSummary: 'Single-phase',
    };
  }

  // Phased mode — CUMULATIVE convention. Each phase's `capacityMW` is the
  // TOTAL (cumulative) site capacity AT that phase's completion, NOT the
  // increment added that phase. The project ULTIMATE total is therefore the
  // FINAL (max-sequence) cumulative value — NOT the sum of phase capacities.
  // (Site-owner decision 2026-06-08: "Total Capacity at Phase Completion".)
  //
  // Defensively use Math.max over finite >0 capacities when the final phase
  // carries an exact capacity: this equals the last phase's value when
  // capacities are monotonic-non-decreasing (the form rule), and degrades
  // gracefully to the largest cumulative figure if a legacy/edited intent is
  // non-monotonic. When the final phase is a range bucket or TBD (`null` MW),
  // `totalCapacityMW` is `null` and `knownCapacityFloorMW` preserves the
  // largest earlier known phase floor separately so an earlier milestone is
  // never reported as the ultimate campus capacity.
  const finiteCapacities = rawPhases
    .map(p => p.capacityMW)
    .filter((mw): mw is number => typeof mw === 'number' && Number.isFinite(mw) && mw > 0);
  const anyCapacity = finiteCapacities.length > 0;
  const knownCapacityFloorMW = anyCapacity ? Math.max(...finiteCapacities) : null;
  const ultimatePhase = rawPhases[rawPhases.length - 1];
  const ultimateHasExact = typeof ultimatePhase.capacityMW === 'number'
    && Number.isFinite(ultimatePhase.capacityMW) && ultimatePhase.capacityMW > 0;
  const ultimateBucket = !ultimateHasExact
    && ultimatePhase.capacityBucket != null && ultimatePhase.capacityBucket !== 'undecided'
    ? ultimatePhase.capacityBucket
    : null;
  const totalMW = ultimateHasExact ? knownCapacityFloorMW : null;
  // Phase-0 cumulative == phase-0 increment == initial capacity — correct
  // under both conventions, so this read is UNCHANGED.
  const initialMW = rawPhases[0].capacityMW ?? null;

  const validDates = rawPhases.map(p => p.targetInServiceDate).filter((d): d is string => typeof d === 'string' && d.length > 0);
  const lastKnownCOD = validDates.length > 0 ? validDates.slice().sort().slice(-1)[0] : null;
  const ultimateHasExactDate = typeof ultimatePhase.targetInServiceDate === 'string'
    && ultimatePhase.targetInServiceDate.length > 0;
  const ultimateCodTimeline = !ultimateHasExactDate
    && ultimatePhase.codTimeline != null && ultimatePhase.codTimeline !== 'undecided'
    ? ultimatePhase.codTimeline
    : null;
  const ultimateCOD = ultimateHasExactDate ? lastKnownCOD : null;
  const initialCOD = rawPhases[0].targetInServiceDate ?? null;

  const sourceMix: PowerSourceKey[] = [];
  const formattedSources: string[] = [];
  for (const p of rawPhases) {
    const formatted = formatPhasePowerSource(p);
    if (formatted !== POWER_SOURCE_LABEL.undecided && !formattedSources.includes(formatted)) {
      formattedSources.push(formatted);
    }
    if (p.powerSource !== 'undecided' && !sourceMix.includes(p.powerSource)) {
      sourceMix.push(p.powerSource);
    }
  }
  const techMixSummary = formattedSources.length === 0
    ? 'Power source TBD across all phases'
    : formattedSources.join(' → ');

  return {
    isPhased: true,
    phaseCount: rawPhases.length,
    phases: rawPhases,
    totalCapacityMW: totalMW,
    ultimateCapacityBucket: ultimateBucket,
    knownCapacityFloorMW,
    initialCapacityMW: initialMW,
    ultimateInServiceDate: ultimateCOD,
    ultimateCodTimeline,
    lastKnownInServiceDate: lastKnownCOD,
    initialInServiceDate: initialCOD,
    powerSourceMix: sourceMix,
    techMixSummary,
  };
}

/**
 * QA-2 (capacity SSOT) — resolve the project's ULTIMATE campus capacity (MW).
 *
 * THE single named entry point for "how big is this project, one number".
 * Under the cumulative phase convention (site-owner decision 2026-06-08,
 * see `effectivePhases` above) each phase's `capacityMW` is the TOTAL site
 * capacity AT that phase's completion, so the ultimate campus capacity is the
 * FINAL (max-sequence) cumulative milestone — NEVER the sum of phase
 * capacities. Summing a 250 / 500 / 1000 MW build double-counts to 1750 MW
 * against a true 1000 MW ultimate (the Holly Ridge / Redhead BLOCKER).
 *
 * Resolution:
 *   - phases exist  → final cumulative milestone (`effectivePhases().totalCapacityMW`,
 *                     defensively Math.max over finite capacities)
 *   - no phases     → `powerRequirementMW` (exact single-phase figure)
 *   - neither known → null (callers must not fabricate)
 */
export function resolveUltimateCampusMW(
  intent: ProjectIntent | undefined | null,
): number | null {
  return effectivePhases(intent).totalCapacityMW;
}

/** How a declared load was expressed. `'none'` ⇒ nothing was declared. */
export type DeclaredLoadBasis = 'phased' | 'exact' | 'bucket' | 'none';

/**
 * Has the user declared a facility load, and on what basis?
 *
 * THREE ways to declare one, and a gate that recognises fewer than three tells
 * a user to declare something they already have:
 *
 *   phased  → `phases[].capacityMW` (cumulative; the top-level MW is
 *             legitimately unset — `reconcilePowerRequirementWithPhases`
 *             treats it as "deliberately cleared, never resurrected")
 *   exact   → `powerRequirementMW`
 *   bucket  → `powerRequirement`, mapped through `bucketMidpointMw`. NOT a
 *             fallback invention: `POWER_BUCKET_MIDPOINT_MW` +
 *             `SIZING_BASIS_RULE` are the platform's documented sizing basis,
 *             already consumed by the power-stack band resolver, financial
 *             cross-validation, the BoM templates and the report auditor.
 *             Switching the intent form to Range mode DELETES the exact MW
 *             (`withPowerRequirementBucket`), so treating a bucket as "no
 *             load" makes Power Availability unrunnable for a site the rest of
 *             the platform considers sized.
 *
 * Only `undecided` — no phases, no MW, no bucket — is genuinely no load. That
 * is the one state where firm-capacity adequacy has no denominator.
 *
 * `mw` is a SIZING BASIS, not a commitment: for a bucket it is a midpoint,
 * band maximum or band floor per `SIZING_BASIS_RULE`. Callers that publish a
 * figure must say which basis produced it; callers that merely need to know
 * whether a load exists should read `basis !== 'none'`.
 */
export function resolveDeclaredLoad(
  intent: ProjectIntent | undefined | null,
): { mw: number | null; basis: DeclaredLoadBasis } {
  const phased = effectivePhases(intent);
  const campus = phased.totalCapacityMW;
  if (typeof campus === 'number' && Number.isFinite(campus) && campus > 0) {
    return { mw: campus, basis: phased.isPhased ? 'phased' : 'exact' };
  }
  const bucketMw = bucketMidpointMw(effectivePower(intent).bucket);
  if (typeof bucketMw === 'number' && Number.isFinite(bucketMw) && bucketMw > 0) {
    return { mw: bucketMw, basis: 'bucket' };
  }
  return { mw: null, basis: 'none' };
}

/** Result of {@link reconcilePowerRequirementWithPhases}. `reconciled: false`
 *  returns the ORIGINAL intent object untouched (identity-preserving no-op). */
export interface PowerRequirementReconciliation {
  intent: ProjectIntent;
  reconciled: boolean;
  /** The contradicting top-level MW that was replaced (null on no-op). */
  fromMW: number | null;
  /** The ultimate campus MW it was reconciled to (null on no-op). */
  toMW: number | null;
  /**
   * Set when the reconcile synced the coarse `powerRequirement` bucket from
   * a RANGE-mode ultimate phase (`ultimateCapacityBucket`). `toMW` is null
   * in that case — a range never reconciles into a scalar. Store glue must
   * adopt `intent` on `reconciled` alone, not on the MW pair.
   */
  toBucket?: PowerBucket;
}

/** Sub-MW float noise tolerance — differences below this are not "drift". */
const CAMPUS_MW_RECONCILE_TOLERANCE_MW = 0.5;

/**
 * QA-2 (capacity SSOT) — reconcile a phased intent's top-level
 * `powerRequirementMW` with its OWN phases' ultimate cumulative milestone.
 *
 * Fixes the double-count BLOCKER where `powerRequirementMW` held the SUM of
 * cumulative phase milestones (250+500+1000 = 1750) instead of the ultimate
 * campus capacity (1000) — every downstream single-figure consumer (prompts'
 * canonical capacity, fidelity scorer `requiredMW`, financial cross-checks,
 * phase-feasibility load fallback) reads `powerRequirementMW` as the ULTIMATE
 * figure, so a summed value contradicts the phases in every report surface.
 *
 * Rules (deliberately narrow — this is a NORMALIZATION of the incoming
 * payload against itself, never a merge against previously stored state, so
 * the wq07loep2 verbatim-write doctrine is preserved):
 *   - no phases                         → no-op (single-phase intents untouched)
 *   - ultimate phase carries a RANGE bucket → sync the coarse top-level bucket
 *     (`withPowerRequirementBucket`, which also deletes any contradicting
 *     exact MW) so every top-level-bucket consumer — SED grounding, site
 *     classifier, overview cards, finDecision — works on phased-range sites
 *     unchanged. A range never reconciles into a scalar (`toMW` stays null).
 *   - phases carry no finite capacity   → no-op (nothing authoritative to apply)
 *   - `powerRequirementMW` unset/cleared → no-op (a deliberate clear via
 *     `withPowerRequirementBucket` is HONORED — we only correct a figure that
 *     is PRESENT and contradicts the phases; we never resurrect or invent one)
 *   - |current − ultimate| < 0.5 MW     → no-op (already coherent)
 *   - otherwise → `withPowerRequirementMW(intent, ultimate)` (bucket follows
 *     per the writer discipline at the top of this file)
 *
 * PROVENANCE: whenever a rewrite replaces a figure that was stamped by the
 * max-power study (`powerSizingMode === 'site-max-power'`), the stamp's
 * provenance is CLEARED (`powerSizingMode` + `fieldSource.powerRequirementMW`)
 * — the figure is no longer study-derived, and keeping the marker would let a
 * phase-derived number masquerade as a max-power result. `intent.maxPowerStudy`
 * (the staleness record) is deliberately KEPT: `isMaxPowerDerivedIntent`
 * requires BOTH mode and stamp, so the derived-figure banner flips off while
 * the study history stays inspectable. A COHERENT stamp (top-level == final
 * phase, the state `buildMaxPowerIntentStamp` writes) is inside the 0.5 MW
 * tolerance → no-op → provenance survives.
 *
 * Pure — never mutates the input.
 */
export function reconcilePowerRequirementWithPhases(
  intent: ProjectIntent,
): PowerRequirementReconciliation {
  const noop: PowerRequirementReconciliation = {
    intent, reconciled: false, fromMW: null, toMW: null,
  };
  if ((intent?.phases?.length ?? 0) === 0) return noop;
  const view = effectivePhases(intent);

  // RANGE-mode ultimate phase — sync the coarse bucket instead of an MW.
  if (view.totalCapacityMW == null && view.ultimateCapacityBucket != null) {
    const bucket = view.ultimateCapacityBucket;
    const current = intent.powerRequirementMW;
    const hasExact = typeof current === 'number' && Number.isFinite(current) && current > 0;
    if (!hasExact && intent.powerRequirement === bucket) return noop; // already coherent
    return {
      intent: clearMaxPowerProvenance(withPowerRequirementBucket(intent, bucket)),
      reconciled: true,
      fromMW: hasExact ? current : null,
      toMW: null, // a range never reconciles into a scalar
      toBucket: bucket,
    };
  }

  // TBD ultimate phase with known earlier-phase floor — clear any stale
  // top-level exact MW or non-undecided bucket so single-figure consumers
  // do not report an earlier milestone or stale intake figure as ultimate.
  if (view.totalCapacityMW == null && view.ultimateCapacityBucket == null && view.knownCapacityFloorMW != null) {
    const current = intent.powerRequirementMW;
    const hasExact = typeof current === 'number' && Number.isFinite(current) && current > 0;
    const hasNonUndecidedBucket = intent.powerRequirement != null && intent.powerRequirement !== 'undecided';
    if (!hasExact && !hasNonUndecidedBucket) return noop;
    return {
      intent: clearMaxPowerProvenance(withPowerRequirementBucket(intent, 'undecided')),
      reconciled: true,
      fromMW: hasExact ? current : null,
      toMW: null,
      toBucket: 'undecided',
    };
  }

  const ultimate = view.totalCapacityMW;
  if (ultimate == null) return noop;
  const current = intent.powerRequirementMW;
  if (typeof current !== 'number' || !Number.isFinite(current) || current <= 0) {
    return noop; // unset / deliberately cleared — honored, never resurrected
  }
  if (Math.abs(current - ultimate) < CAMPUS_MW_RECONCILE_TOLERANCE_MW) return noop;
  return {
    intent: clearMaxPowerProvenance(withPowerRequirementMW(intent, ultimate)),
    reconciled: true,
    fromMW: current,
    toMW: ultimate,
  };
}

/**
 * Drop the max-power stamp's PROVENANCE (never its staleness record) when a
 * reconcile rewrites the stamped figure. See the provenance note on
 * `reconcilePowerRequirementWithPhases`. The `'site-max-power'` literal is
 * typed by `ProjectIntent.powerSizingMode` — deliberately NOT imported from
 * `maxPowerIntentStamp.ts` (that module imports from this one; an import
 * back would be a cycle). No-op (same reference) when the intent carries no
 * max-power provenance.
 */
function clearMaxPowerProvenance(intent: ProjectIntent): ProjectIntent {
  if (intent.powerSizingMode !== 'site-max-power') return intent;
  const { powerSizingMode: _mode, ...rest } = intent;
  void _mode;
  const next: ProjectIntent = { ...rest };
  if (next.fieldSource && 'powerRequirementMW' in next.fieldSource) {
    const { powerRequirementMW: _src, ...restSources } = next.fieldSource;
    void _src;
    next.fieldSource = restSources as ProjectIntent['fieldSource'];
  }
  return next;
}

/**
 * SSOT for the per-phase capacity INCREMENT under the cumulative convention.
 *
 * Each phase's stored `capacityMW` is the CUMULATIVE site capacity at that
 * phase's completion (site-owner decision 2026-06-08). The capacity ADDED in
 * phase `i` (the new generation / equipment that phase procures, and the
 * energy basis its new plant produces) is therefore the DIFFERENCE:
 *
 *     increment_i = cap_i − cap_{i−1}        (i > 0)
 *     increment_0 = cap_0                     (first phase: cumulative == increment)
 *
 * Every consumer that needs "MW added this phase" — CAPEX/OPEX allocation,
 * BoM equipment sizing, per-phase energy/LCOE blending, $/kW divisors,
 * sensitivity weights — MUST call this helper instead of reading
 * `phase.capacityMW` directly for additive math. Consumers that need the
 * project TOTAL use `effectivePhases().totalCapacityMW` (final/max), and
 * consumers that need the datacenter LOAD AT a phase (a cumulative figure)
 * read `phase.capacityMW` directly.
 *
 * Floored at 0 so a non-monotonic / mid-edit intent (where a later phase was
 * mistakenly entered smaller than an earlier one) never yields negative MW —
 * and therefore never negative CAPEX/energy.
 *
 * Accepts any phase shape carrying `sequence` + `capacityMW` so the same SSOT
 * serves both `ProjectPhase[]` (intent) and `FinancialPhase[]` (finance).
 *
 * @param phases  Phase array (any order — sorted defensively by `sequence`).
 * @param i       Index INTO THE SORTED array (0-based).
 * @returns       Increment MW (>= 0), or null when phase `i`'s capacity is null.
 */
export function phaseIncrementMW(
  phases: ReadonlyArray<{ sequence: number; capacityMW: number | null }>,
  i: number,
): number | null {
  const sorted = phases.slice().sort((a, b) => a.sequence - b.sequence);
  const cur = sorted[i]?.capacityMW;
  if (cur == null || !Number.isFinite(cur)) return null;
  if (i === 0) return Math.max(0, cur);
  const prevRaw = sorted[i - 1]?.capacityMW;
  // A null/non-finite prior cumulative is treated as 0 (nothing built yet).
  const prev = prevRaw != null && Number.isFinite(prevRaw) ? prevRaw : 0;
  return Math.max(0, cur - prev);
}

/**
 * SSOT for the per-SOURCE capacity INCREMENT — the per-source analogue of
 * `phaseIncrementMW` (above).
 *
 * Under the carry-forward convention each phase carries ALL existing
 * infrastructure forward, so a source's stored
 * `powerSourceConfig.componentParams[sourceKey].capacityMW` is the CUMULATIVE
 * capacity of THAT source at that phase's completion (mirroring the cumulative
 * `phase.capacityMW`). The capacity ADDED for a source in phase `i` — the new
 * generation that phase procures (the basis for that phase's per-source CAPEX,
 * BoM equipment, energy and LCOE) — is therefore the DIFFERENCE:
 *
 *     perSourceIncrement_i = cumulative_i - cumulative_{i-1}   (i >= 1)
 *     perSourceIncrement_0 = cumulative_0                      (first phase)
 *
 * Every per-phase, per-source ADDITIVE consumer (CAPEX / energy / LCOE /
 * BoM-equipment-per-source) MUST call this instead of reading
 * `componentParams[sourceKey].capacityMW` directly. Consumers that need the
 * cumulative FIRM/LOAD requirement at a phase (e.g. `assessPhaseFeasibility`)
 * read the cumulative `componentParams[sourceKey].capacityMW` directly and must
 * NOT difference. CF (`capacityFactorPct`) is an intensive RATE and is never
 * differenced — it carries forward per source unchanged.
 *
 * A source ABSENT in the prior phase (never built before this phase) is treated
 * as prev = 0, so its FIRST appearance books its full cumulative as the
 * increment. Floored at 0 so a mid-edit / non-monotonic per-source value never
 * yields negative MW / CAPEX / energy.
 *
 * This NEVER touches `phase.capacityMW` or `effectivePhases().totalCapacityMW`
 * — the cumulative-phase invariant is left intact.
 *
 * @param phases     Phase array (any order — sorted defensively by `sequence`).
 * @param sourceKey  The power-source component to difference.
 * @param i          Index INTO THE SORTED array (0-based).
 * @returns          Increment MW (>= 0), or null when this phase carries no
 *                   finite cumulative capacity for `sourceKey`.
 */
export function perSourceIncrementMW(
  phases: ReadonlyArray<{ sequence: number; powerSourceConfig?: PhasePowerSourceConfig }>,
  sourceKey: PhasePowerComponentKey,
  i: number,
): number | null {
  const sorted = phases.slice().sort((a, b) => a.sequence - b.sequence);
  const cur = sorted[i]?.powerSourceConfig?.componentParams?.[sourceKey]?.capacityMW;
  if (cur == null || !Number.isFinite(cur)) return null;
  if (i === 0) return Math.max(0, cur);
  const prevRaw = sorted[i - 1]?.powerSourceConfig?.componentParams?.[sourceKey]?.capacityMW;
  // A source absent (never built) / null in the prior phase is treated as 0, so
  // its first appearance's full cumulative becomes its increment.
  const prev = prevRaw != null && Number.isFinite(prevRaw) ? prevRaw : 0;
  return Math.max(0, cur - prev);
}

/**
 * Resolve the IT-side cooling strategy for a phase.
 *
 * The per-phase cooling override was removed from the Project Intent form, so
 * the IT cooling loop is now a SITE-LEVEL decision only — every phase resolves
 * to `intent.coolingStrategy`. The `phase` parameter is retained for call-site
 * compatibility (and signature symmetry with the other `effectivePhase*`
 * resolvers) but no longer participates in resolution.
 */
export function effectivePhaseCooling(_phase: ProjectPhase, intent: ProjectIntent): NonNullable<ProjectIntent['coolingStrategy']> {
  return intent.coolingStrategy ?? 'undecided';
}

/**
 * IC2 — canonical cooling resolver. Returns the projected `CoolingType`
 * for the given phase + intent pair. Composes `effectivePhaseCooling`
 * (legacy enum fallback chain) with `normaliseCooling` (boundary
 * projection onto the canonical type). Callers that need the canonical
 * type should prefer this over reading `intent.coolingStrategy` directly
 * so the three legacy enum vocabularies collapse to one.
 */
export function effectivePhaseCoolingCanonical(
  phase: ProjectPhase,
  intent: ProjectIntent,
): CoolingType {
  return normaliseCooling(effectivePhaseCooling(phase, intent));
}

/** Resolve per-phase workload profile, falling back to top-level intent. */
export function effectivePhaseWorkload(
  phase: ProjectPhase,
  intent: ProjectIntent,
): NonNullable<ProjectIntent['workloadProfile']> {
  return phase.workloadProfile ?? intent.workloadProfile ?? 'mixed';
}

/** Resolve per-phase power source (no fallback — `powerSource` is required on the phase). */
export function effectivePhaseTech(phase: ProjectPhase): PowerSourceKey {
  return phase.powerSource;
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-phase COD timeline — mirrors top-level `effectiveTimeline` (PP pack).
// ─────────────────────────────────────────────────────────────────────────────

/** Resolved COD view for a single phase. */
export interface EffectivePhaseTimeline {
  inServiceDate: string | null;
  bucket: TimelineBucket;
  isExact: boolean;
  displayLabel: string;
}

/** Resolve the precision-preserving COD view of a phase. */
export function effectivePhaseTimeline(
  phase: Pick<ProjectPhase, 'targetInServiceDate' | 'codTimeline'>,
  now: Date = new Date(),
): EffectivePhaseTimeline {
  const iso = phase.targetInServiceDate;
  if (iso) {
    const target = new Date(iso);
    if (Number.isFinite(target.getTime())) {
      return {
        inServiceDate: iso,
        bucket: bucketFromInServiceDate(iso, now),
        isExact: true,
        displayLabel: formatInServiceDate(iso),
      };
    }
  }
  const bucket = (phase.codTimeline ?? 'undecided') as TimelineBucket;
  return {
    inServiceDate: null,
    bucket,
    isExact: false,
    displayLabel: TIMELINE_BUCKET_LABEL[bucket] ?? TIMELINE_BUCKET_LABEL.undecided,
  };
}

/** Writer helper — sets phase exact COD and recomputes the bucket. */
export function withPhaseInServiceDate(
  phase: ProjectPhase,
  inServiceDate: string,
  now: Date = new Date(),
): ProjectPhase {
  return {
    ...phase,
    targetInServiceDate: inServiceDate,
    codTimeline: bucketFromInServiceDate(inServiceDate, now) as PhaseCodTimeline,
  };
}

/** Writer helper — clears exact COD and sets the coarse bucket. */
export function withPhaseTimelineBucket(
  phase: ProjectPhase,
  bucket: TimelineBucket,
): ProjectPhase {
  return {
    ...phase,
    targetInServiceDate: null,
    codTimeline: bucket as PhaseCodTimeline,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-phase capacity — exact / range / max-derived trio, mirroring both the
// top-level `powerRequirementMW`/`powerRequirement` pair and the per-phase
// COD pair above. `capacityMW` stays the ONLY scalar (cumulative-at-completion);
// `capacityBucket` carries a range; `capacitySource` marks a max-power-derived
// figure. The writers are the only sanctioned mutation path — they keep the
// trio consistent (exact ⊕ bucket, provenance dropped on manual edits).
// ─────────────────────────────────────────────────────────────────────────────

/** Resolved capacity view for a single phase. */
export interface EffectivePhaseCapacity {
  /** Exact CUMULATIVE MW when `capacityMW` is set; null in range/undecided mode. */
  mw: number | null;
  /** Always populated — derived from `mw` when exact, else the stored bucket. */
  bucket: PowerBucket;
  /** True when `capacityMW` carries a finite figure. */
  isExact: boolean;
  /** "350 MW" if exact, else the bucket label ("200–500 MW", "Undecided", …). */
  displayLabel: string;
  /** [min, max] MW — collapses to {mw, mw} when exact; null when undecided.
   *  Open bands carry 0 / Infinity edges (see `bucketMwRange`). */
  range: { min: number; max: number } | null;
  /** Single-figure sizing basis — the exact MW, or the bucket's documented
   *  basis (`POWER_BUCKET_MIDPOINT_MW`). Null when undecided. */
  sizingBasisMW: number | null;
  /** Which rule produced `sizingBasisMW` for a bucket; null when exact. */
  sizingBasisRule: SizingBasisRule | null;
  /** 'site-max-power-study' when the exact figure was stamped by the
   *  max-power study rather than typed. Null otherwise. */
  source: 'site-max-power-study' | null;
}

/** Resolve the precision-preserving capacity view of a phase. Exact wins;
 *  a stored `capacityBucket` only speaks when no exact figure is present
 *  (mirrors `effectivePower` / `effectivePhaseTimeline`). */
export function effectivePhaseCapacity(
  phase: Pick<ProjectPhase, 'capacityMW' | 'capacityBucket' | 'capacitySource'>,
): EffectivePhaseCapacity {
  const mw = phase.capacityMW;
  if (typeof mw === 'number' && Number.isFinite(mw) && mw > 0) {
    return {
      mw,
      bucket: bucketFromMw(mw),
      isExact: true,
      displayLabel: formatMw(mw),
      range: { min: mw, max: mw },
      sizingBasisMW: mw,
      sizingBasisRule: null,
      source: phase.capacitySource ?? null,
    };
  }
  const bucket = (phase.capacityBucket ?? 'undecided') as PowerBucket;
  if (bucket === 'undecided') {
    return {
      mw: null,
      bucket,
      isExact: false,
      displayLabel: POWER_BUCKET_LABEL.undecided,
      range: null,
      sizingBasisMW: null,
      sizingBasisRule: null,
      source: null,
    };
  }
  return {
    mw: null,
    bucket,
    isExact: false,
    displayLabel: POWER_BUCKET_LABEL[bucket],
    range: bucketMwRange(bucket),
    sizingBasisMW: bucketMidpointMw(bucket) ?? null,
    sizingBasisRule: SIZING_BASIS_RULE[bucket],
    source: null,
  };
}

/** Writer helper — sets the exact cumulative MW (null while drafting) and
 *  drops any range bucket AND any derived-figure provenance: a manual edit
 *  means the figure is user-declared again. */
export function withPhaseCapacityMW(phase: ProjectPhase, mw: number | null): ProjectPhase {
  const { capacityBucket: _bucket, capacitySource: _source, ...rest } = phase;
  void _bucket; void _source;
  return { ...rest, capacityMW: mw };
}

/** Writer helper — switches the phase to RANGE mode: clears the exact MW
 *  (required-nullable → null) and any derived-figure provenance, sets the
 *  bucket. Mirrors `withPowerRequirementBucket`. */
export function withPhaseCapacityBucket(phase: ProjectPhase, bucket: PowerBucket): ProjectPhase {
  const { capacitySource: _source, ...rest } = phase;
  void _source;
  return { ...rest, capacityMW: null, capacityBucket: bucket };
}

/** Writer helper — adopts the max-power study's facility MW as this phase's
 *  CUMULATIVE capacity, with provenance. Only meaningful on the ULTIMATE
 *  phase (the cumulative convention makes "max" the build-out end-state);
 *  callers enforce that placement. Drops any range bucket. */
export function withPhaseCapacityMax(phase: ProjectPhase, facilityMW: number): ProjectPhase {
  const { capacityBucket: _bucket, ...rest } = phase;
  void _bucket;
  return { ...rest, capacityMW: facilityMW, capacitySource: 'site-max-power-study' };
}

const SINGLE_COMPONENT_POWER_SOURCE: Record<PhasePowerComponentKey, PowerSourceKey> = {
  'recip': 'gas-sc',
  'gas-turbine': 'gas-cc',
  'fuel-cell': 'hybrid',
  'battery': 'hybrid',
  'grid': 'grid',
  'solar-bess': 'solar-bess',
  'wind-bess': 'wind-bess',
  'smr': 'smr',
  'large-nuclear': 'large-nuclear',
};

/** Derive the coarse `powerSource` key from a hybrid config. */
export function syncPowerSourceFromConfig(
  config: PhasePowerSourceConfig | undefined,
): PowerSourceKey {
  const components = config?.components ?? [];
  if (components.length === 0) return 'undecided';
  if (components.length === 1) return SINGLE_COMPONENT_POWER_SOURCE[components[0]];
  return 'hybrid';
}

/**
 * Prove that every declared phase selects a power mix without natural gas.
 * `fuelAccess` describes gas supply, not liquid-fuel standby. Only explicit,
 * known component mixes with their matching canonical power-source key can
 * remove a gas constraint; missing, legacy or contradictory mixes fail closed.
 * This does not establish fuel deliverability or change capacity/ELCC rules.
 */
export function intentHasExplicitGasFreePowerMix(
  intent: ProjectIntent | undefined | null,
): boolean {
  const phases = intent?.phases;
  if (!Array.isArray(phases) || phases.length === 0) return false;
  for (const phase of phases) {
    const config = phase?.powerSourceConfig;
    const components = config?.components;
    if (!config || !Array.isArray(components) || components.length === 0) return false;
    if (config.mixMode !== undefined && config.mixMode !== 'and' && config.mixMode !== 'or') return false;
    for (const component of components) {
      if (component !== 'grid' && component !== 'battery' && component !== 'solar-bess'
        && component !== 'wind-bess' && component !== 'smr' && component !== 'large-nuclear') return false;
    }
    if (phase.powerSource !== syncPowerSourceFromConfig(config)) return false;
  }
  return true;
}

/** Human-readable power source label for a phase (hybrid config or legacy key). */
export function formatPhasePowerSource(phase: ProjectPhase): string {
  const cfg = phase.powerSourceConfig;
  if (cfg && cfg.components.length > 0) {
    const parts = cfg.components.map(c => PHASE_POWER_COMPONENT_LABEL[c]);
    // mixMode reflects whether the sources COMBINE ('and' → '+') or are
    // mutually-exclusive ALTERNATIVES ('or' → ' or '). Absent = legacy 'or'.
    const joiner = cfg.mixMode === 'and' ? ' + ' : ' or ';
    const prefix = cfg.mixMode === 'and' ? 'AND: ' : cfg.mixMode === 'or' ? 'OR: ' : '';
    let summary = prefix + parts.join(joiner);
    if (cfg.gridConnection === 'islanded') {
      summary += ', islanded';
    } else if (cfg.gridConnection === 'grid-connected') {
      summary += cfg.gridUtilityName
        ? `, ${cfg.gridUtilityName} grid`
        : ', grid-connected';
    }
    return summary;
  }
  return POWER_SOURCE_LABEL[phase.powerSource] ?? phase.powerSource;
}

/**
 * Plain-English component nouns for READER-FACING cover/prose copy.
 *
 * Distinct from `PHASE_POWER_COMPONENT_LABEL` (terse editor chips like
 * "Recip engines") — these read as natural language inside a sentence
 * ("reciprocating gas engines", "battery storage"). Used ONLY by the
 * humanized cover formatters below; the editor labels stay unchanged.
 */
const HUMAN_POWER_COMPONENT_NOUN: Record<PhasePowerComponentKey, string> = {
  'recip':         'reciprocating gas engines',
  'gas-turbine':   'gas turbines',
  'fuel-cell':     'stationary fuel cells',
  'battery':       'battery storage',
  'grid':          'grid import',
  'solar-bess':    'solar with battery storage',
  'wind-bess':     'wind with battery storage',
  'smr':           'small modular reactors',
  'large-nuclear': 'large nuclear',
};

/** Sentence-case the first letter of a prose fragment. */
function sentenceCaseFirst(text: string): string {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Humanize a single phase's power mix into reader-facing prose — NO editor
 * notation (no "AND:"/"OR:" prefixes, no terse ", grid-connected").
 *
 * Examples:
 *   - { components: ['grid','recip'], gridConnection: 'grid-connected' }
 *       → "Grid import (grid-connected), bridged by reciprocating gas engines"
 *   - { components: ['smr','battery'], mixMode: 'and', gridConnection: 'islanded' }
 *       → "Small modular reactors combined with battery storage (islanded microgrid)"
 *
 * Falls back to the canonical `POWER_SOURCE_LABEL` when there is no hybrid
 * config (legacy single-source phases) so output is always reader-clean.
 */
export function humanizePowerMix(phase: ProjectPhase): string {
  const cfg = phase.powerSourceConfig;
  if (!cfg || cfg.components.length === 0) {
    return POWER_SOURCE_LABEL[phase.powerSource] ?? phase.powerSource;
  }

  const nouns = cfg.components.map(c => HUMAN_POWER_COMPONENT_NOUN[c] ?? c);

  // Grid-connection qualifier as a natural parenthetical (not the terse
  // ", grid-connected" editor suffix).
  let qualifier = '';
  if (cfg.gridConnection === 'grid-connected') {
    qualifier = cfg.gridUtilityName
      ? ` (grid-connected to ${cfg.gridUtilityName})`
      : ' (grid-connected)';
  } else if (cfg.gridConnection === 'islanded') {
    qualifier = ' (islanded microgrid)';
  }

  // Grid is best read as the connection anchor when it leads the mix; the
  // remaining on-site sources "bridge" / firm it. The qualifier attaches to
  // the grid anchor. Otherwise the sources combine ('and') or are
  // alternatives ('or'), with the qualifier trailing the whole body.
  const hasGrid = cfg.components.includes('grid');
  const gridIdx = cfg.components.indexOf('grid');

  if (hasGrid && cfg.components.length > 1 && gridIdx === 0) {
    const rest = nouns.slice(1);
    const restJoined = rest.length === 1
      ? rest[0]
      : `${rest.slice(0, -1).join(', ')} and ${rest[rest.length - 1]}`;
    return sentenceCaseFirst(`${nouns[0]}${qualifier}, bridged by ${restJoined}`);
  }

  let body: string;
  if (nouns.length === 1) {
    body = nouns[0];
  } else {
    const joiner = cfg.mixMode === 'and' ? ' combined with ' : ' or ';
    body = nouns.length === 2
      ? nouns.join(joiner)
      : `${nouns.slice(0, -1).join(', ')}${joiner.trimEnd()} ${nouns[nouns.length - 1]}`;
  }

  return sentenceCaseFirst(`${body}${qualifier}`);
}

/**
 * Cover-ready Primary-BTM summary from a phased intent — humanized prose
 * across phases, joined with an arrow. Returns `null` for non-phased or
 * all-TBD intents (so the cover chip can collapse). The editor internals
 * (`formatPhasePowerSource`, `effectivePhases().techMixSummary`) are left
 * untouched — this is the reader-facing seam ONLY.
 */
export function formatPrimaryBtmForCover(intent: ProjectIntent | undefined): string | null {
  if (!intent) return null;
  const resolved = effectivePhases(intent);
  if (!resolved.isPhased) return null;
  const parts: string[] = [];
  for (const phase of resolved.phases) {
    const isUndecided = phase.powerSource === 'undecided'
      && (!phase.powerSourceConfig || phase.powerSourceConfig.components.length === 0);
    if (isUndecided) continue;
    const human = humanizePowerMix(phase);
    if (human && !parts.includes(human)) parts.push(human);
  }
  if (parts.length === 0) return null;
  return parts.join(' → ');
}

/**
 * Apply a hybrid power config and sync the legacy `powerSource` field.
 *
 * The full `config` is stored verbatim, so the optional `mixMode` and
 * `componentParams` (AND/OR feasibility inputs) are carried through unchanged.
 */
export function withPhasePowerSourceConfig(
  phase: ProjectPhase,
  config: PhasePowerSourceConfig,
): ProjectPhase {
  return {
    ...phase,
    powerSourceConfig: config,
    powerSource: syncPowerSourceFromConfig(config),
  };
}

/**
 * CARRY-FORWARD — build the power-source config a NEW phase should start from by
 * deep-copying the PRIOR phase's config, so the user keeps all existing
 * infrastructure (the grid / recip / etc. they already selected) without
 * re-picking it every phase.
 *
 * Because the stored `componentParams[s].capacityMW` is CUMULATIVE-at-phase
 * (matching the cumulative-phase convention at f321c33c), copying it forward
 * VERBATIM is correct: the new phase STARTS at the prior cumulative per source,
 * so every carried source's per-source increment (`perSourceIncrementMW`) is 0
 * until the user edits its cumulative MW UP. CF carries forward unchanged (an
 * intensive rate).
 *
 * Deep-copies so editing the new phase never mutates the prior phase's stored
 * objects: `components[]` is cloned, `componentParams` is rebuilt with a fresh
 * `{ capacityMW, capacityFactorPct }` per key, and the scalar fields
 * (`gridConnection`, `gridUtilityName`, `mixMode`) are copied. Returns
 * `undefined` when the prior phase has no `powerSourceConfig` (so phase 1 stays
 * a clean slate via `makeEmptyPhase`).
 */
export function carryForwardPowerConfig(
  prevPhase: ProjectPhase,
): PhasePowerSourceConfig | undefined {
  const prev = prevPhase.powerSourceConfig;
  if (!prev) return undefined;

  const componentParams: NonNullable<PhasePowerSourceConfig['componentParams']> = {};
  if (prev.componentParams) {
    for (const key of Object.keys(prev.componentParams) as PhasePowerComponentKey[]) {
      const p = prev.componentParams[key];
      if (!p) continue;
      // Fresh object per key so the new phase never aliases the prior phase's
      // stored params (editing one never mutates the other). ALLOW-LIST — a
      // new per-source field silently vanishes on "Add phase" unless carried
      // here explicitly (capacityMinMW/capacityMaxMW: a carried range stays a
      // range; the per-source Δ chip is inherently null against a band).
      componentParams[key] = {
        ...(p.capacityMW != null ? { capacityMW: p.capacityMW } : {}),
        ...(p.capacityFactorPct != null ? { capacityFactorPct: p.capacityFactorPct } : {}),
        ...(p.capacityMinMW != null ? { capacityMinMW: p.capacityMinMW } : {}),
        ...(p.capacityMaxMW != null ? { capacityMaxMW: p.capacityMaxMW } : {}),
      };
    }
  }

  return {
    components: [...prev.components],
    gridConnection: prev.gridConnection,
    ...(prev.gridUtilityName != null ? { gridUtilityName: prev.gridUtilityName } : {}),
    ...(prev.mixMode != null ? { mixMode: prev.mixMode } : {}),
    componentParams,
  };
}

/* ── Per-source capacity duality (exact ⊕ free min–max range) ─────────────
 *
 * The per-source axis mirrors the phase-level `capacityMW ⊕ capacityBucket`
 * contract, but with FREE ranges (equipment-scale numbers, no bucket ladder).
 * Never read `componentParams[k].capacity*` raw — resolve through
 * `effectivePhaseSourceCapacity` so exact-wins, drafting states and the
 * labeled sizing basis are enforced in exactly one place.
 */

export interface EffectivePhaseSourceCapacity {
  component: PhasePowerComponentKey;
  /** 'exact' when capacityMW carries a finite positive figure (exact WINS);
   *  'range' when both edges are valid (0 ≤ min ≤ max, max > 0);
   *  'unsized' otherwise — including half-set / inverted drafting states. */
  kind: 'exact' | 'range' | 'unsized';
  /** Exact cumulative MW — exact mode only. */
  mw: number | null;
  /** Range edges — range mode only, always finite. */
  minMW: number | null;
  maxMW: number | null;
  /** Deterministic single-figure basis: the exact MW, or the range's true
   *  midpoint (free ranges are closed by construction — no open bands, so
   *  none of the bucket ladder's open-edge rules apply). */
  sizingBasisMW: number | null;
  sizingBasisRule: 'declared-exact' | 'range-midpoint' | null;
  isExact: boolean;
  /** "350 MW" | "200–500 MW" | "unsized". */
  displayLabel: string;
}

/** Resolve one source's capacity view for a phase. Exact wins; a stored
 *  range only speaks when no exact figure is present. */
export function effectivePhaseSourceCapacity(
  phase: Pick<ProjectPhase, 'powerSourceConfig'>,
  key: PhasePowerComponentKey,
): EffectivePhaseSourceCapacity {
  const p = phase.powerSourceConfig?.componentParams?.[key];
  const mw = p?.capacityMW;
  if (typeof mw === 'number' && Number.isFinite(mw) && mw > 0) {
    return {
      component: key,
      kind: 'exact',
      mw,
      minMW: null,
      maxMW: null,
      sizingBasisMW: mw,
      sizingBasisRule: 'declared-exact',
      isExact: true,
      displayLabel: formatMw(mw),
    };
  }
  const min = p?.capacityMinMW;
  const max = p?.capacityMaxMW;
  const rangeValid =
    typeof min === 'number' && Number.isFinite(min) && min >= 0 &&
    typeof max === 'number' && Number.isFinite(max) && max > 0 &&
    min <= max;
  if (rangeValid) {
    return {
      component: key,
      kind: 'range',
      mw: null,
      minMW: min,
      maxMW: max,
      sizingBasisMW: (min + max) / 2,
      sizingBasisRule: 'range-midpoint',
      isExact: false,
      displayLabel: `${formatMw(min).replace(/ MW$/, '')}–${formatMw(max)}`,
    };
  }
  return {
    component: key,
    kind: 'unsized',
    mw: null,
    minMW: null,
    maxMW: null,
    sizingBasisMW: null,
    sizingBasisRule: null,
    isExact: false,
    displayLabel: 'unsized',
  };
}

/** Writer — set (or clear, `null`) a source's EXACT cumulative MW. Drops the
 *  range edges (exact wins is enforced at write time, mirroring
 *  `withPhaseCapacityMW`). Preserves `capacityFactorPct`.
 *
 *  `source` stamps derived-figure provenance (the phase power plan's Apply
 *  path). OMITTED on every manual edit — the marker is then DROPPED, so a
 *  hand-typed MW is user-declared again (marker describes history, never
 *  authority). */
export function withPhaseSourceCapacityMW(
  phase: ProjectPhase,
  key: PhasePowerComponentKey,
  mw: number | null,
  source?: 'phase-power-plan',
): ProjectPhase {
  const cfg = phase.powerSourceConfig;
  if (!cfg) return phase;
  const prev = cfg.componentParams?.[key] ?? {};
  const { capacityMW: _mw, capacityMinMW: _min, capacityMaxMW: _max, capacitySource: _src, ...rest } = prev;
  void _mw; void _min; void _max; void _src;
  return withPhasePowerSourceConfig(phase, {
    ...cfg,
    componentParams: {
      ...(cfg.componentParams ?? {}),
      [key]: {
        ...rest,
        ...(mw != null && Number.isFinite(mw) ? { capacityMW: mw } : {}),
        ...(mw != null && Number.isFinite(mw) && source ? { capacitySource: source } : {}),
      },
    },
  });
}

/** Writer — set a source's capacity RANGE edges (either may be null while the
 *  user is drafting; the resolver reports 'unsized' until both are valid).
 *  Drops the exact figure. Preserves `capacityFactorPct`. */
export function withPhaseSourceCapacityRange(
  phase: ProjectPhase,
  key: PhasePowerComponentKey,
  minMW: number | null,
  maxMW: number | null,
): ProjectPhase {
  const cfg = phase.powerSourceConfig;
  if (!cfg) return phase;
  const prev = cfg.componentParams?.[key] ?? {};
  // capacitySource dropped too: a manual range edit replaces any plan-written
  // figure — the provenance marker never outlives the value it described.
  const { capacityMW: _mw, capacityMinMW: _min, capacityMaxMW: _max, capacitySource: _src, ...rest } = prev;
  void _mw; void _min; void _max; void _src;
  return withPhasePowerSourceConfig(phase, {
    ...cfg,
    componentParams: {
      ...(cfg.componentParams ?? {}),
      [key]: {
        ...rest,
        ...(minMW != null && Number.isFinite(minMW) ? { capacityMinMW: minMW } : {}),
        ...(maxMW != null && Number.isFinite(maxMW) ? { capacityMaxMW: maxMW } : {}),
      },
    },
  });
}

/** Dynamic timeline tip text derived from per-phase COD precision. */
export function phaseTimelineTipText(phases: ProjectPhase[] | undefined | null): string {
  const DEFAULT =
    'Tip: enter the exact target COD date if you know it. Reports cite the exact '
    + 'month in critical-path analysis and procurement scheduling. Use a range '
    + 'only when timing isn\'t decided yet.';

  const list = phases ?? [];
  if (list.length === 0) return DEFAULT;

  const modes = list.map(p => {
    const t = effectivePhaseTimeline(p);
    if (t.isExact) return 'exact';
    if (t.bucket === 'undecided') return 'undecided';
    return 'range';
  });

  const exactCount = modes.filter(m => m === 'exact').length;
  const rangeCount = modes.filter(m => m === 'range').length;
  const undecidedCount = modes.filter(m => m === 'undecided').length;
  const total = list.length;

  if (exactCount === total) {
    return 'Tip: all phases have exact COD dates. Reports will cite each phase\'s '
      + 'target month in critical-path analysis, procurement scheduling, and '
      + 'interconnection sequencing.';
  }
  if (undecidedCount === total) {
    return 'Tip: all phase CODs are undecided. Use exact dates when you know them, '
      + 'or pick a range bucket per phase to signal planning horizon without '
      + 'committing to a month.';
  }
  if (rangeCount === total) {
    return 'Tip: all phases use range buckets. Reports will treat timing as approximate '
      + '— switch any phase to an exact date once you know the target month.';
  }

  const parts: string[] = [];
  if (exactCount > 0) parts.push(`${exactCount} exact`);
  if (rangeCount > 0) parts.push(`${rangeCount} range`);
  if (undecidedCount > 0) parts.push(`${undecidedCount} undecided`);
  return `Tip: mixed COD precision across phases (${parts.join(', ')}). Reports use `
    + 'exact months where set and range buckets elsewhere. Add exact dates to '
    + 'remaining phases as they firm up.';
}

/**
 * Pure helper — build a fresh, stable phase id. Uses `crypto.randomUUID`
 * when available, falls back to a time-based id for older runtimes.
 * Centralised so test mocks can pin it.
 */
export function newPhaseId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `phase-${crypto.randomUUID()}`;
    }
  } catch { /* ignore */ }
  return `phase-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Build an empty phase scaffold with sane defaults — used by the form
 * "Add phase" CTA and by tests. `sequence` is 1-based; callers should
 * pass the next available sequence number.
 */
export function makeEmptyPhase(sequence: number): ProjectPhase {
  return {
    id: newPhaseId(),
    label: `Phase ${sequence}`,
    sequence,
    capacityMW: null,
    targetInServiceDate: null,
    codTimeline: 'undecided',
    powerSource: 'undecided',
  };
}

/**
 * Detect dependency cycles in a phase list using DFS. Returns the list
 * of phase ids that participate in any cycle (empty when acyclic). Used
 * by form validation in PP3.
 */
export function findPhaseDependencyCycles(phases: ProjectPhase[]): string[] {
  const idToPhase = new Map<string, ProjectPhase>();
  for (const p of phases) idToPhase.set(p.id, p);
  const visiting = new Set<string>();
  const visited = new Set<string>();
  const inCycle = new Set<string>();

  function dfs(id: string, stack: string[]): void {
    if (visited.has(id)) return;
    if (visiting.has(id)) {
      // Found cycle — every node from the matching stack entry onwards is in it.
      const idx = stack.indexOf(id);
      if (idx >= 0) for (const s of stack.slice(idx)) inCycle.add(s);
      return;
    }
    visiting.add(id);
    const p = idToPhase.get(id);
    if (p?.dependencies) {
      for (const dep of p.dependencies) {
        if (idToPhase.has(dep)) dfs(dep, [...stack, id]);
      }
    }
    visiting.delete(id);
    visited.add(id);
  }

  for (const p of phases) dfs(p.id, []);
  return Array.from(inCycle);
}
